import { createContext, useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import apiClient from "../utils/axios";

interface OrganizationInfo {
  id: string;
  name: string;
  subscription_status?: string;
  free_trial_expires_at?: string;
  is_owner: boolean;
}

type OrganizationContextType = {
  organizations: OrganizationInfo[];
  loading: boolean;
  isOrganizationOwner: boolean;
  ownedOrganizations: OrganizationInfo[];
  refreshOrganizations: () => Promise<void>;
};

const OrganizationContext = createContext<OrganizationContextType>({
  organizations: [],
  loading: true,
  isOrganizationOwner: false,
  ownedOrganizations: [],
  refreshOrganizations: async () => {},
});

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const { getAccessTokenSilently, isLoading: authLoading, isAuthenticated } = useAuth0();

  const fetchOrganizations = async () => {
    if (authLoading || !isAuthenticated) return;

    try {
      console.log("[OrganizationContext] Fetching organizations from API...");
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get("/api/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // The profile endpoint returns organizations with a different structure
      const organizations = response.data.organizations || [];
      
      // Map the profile data structure to our expected structure
      const mappedOrganizations = organizations.map((org: any) => ({
        id: org.id,
        name: org.name,
        subscription_status: org.subscription_status,
        free_trial_expires_at: org.free_trial_expires_at,
        is_owner: org.role === 'Owner' // Check if role is 'Owner'
      }));
      
      console.log("[OrganizationContext] Setting organizations:", {
        count: mappedOrganizations.length,
        ownedCount: mappedOrganizations.filter((org: OrganizationInfo) => org.is_owner).length,
        organizations: mappedOrganizations.map((org: OrganizationInfo) => ({ id: org.id, name: org.name, is_owner: org.is_owner }))
      });
      
      setOrganizations(mappedOrganizations);
    } catch (error) {
      console.error("Failed to fetch organizations", error);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [authLoading, isAuthenticated]);

  const isOrganizationOwner = organizations.some(org => org.is_owner);
  const ownedOrganizations = organizations.filter(org => org.is_owner);

  return (
    <OrganizationContext.Provider value={{ 
      organizations, 
      loading, 
      isOrganizationOwner,
      ownedOrganizations,
      refreshOrganizations: fetchOrganizations
    }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => useContext(OrganizationContext); 